import { useMediaQuery } from "@mui/material";
// TODO: send a credential error toast when 401.
import { useTheme } from "design-system/themeContext";
import { getIsDefaultTheme } from "design-system/themeSelection";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { setIsInactivelyLoggedOut, setUser } from "store/slices/user/userSlice";

import { loginUser } from "../api/functions/all";
import apiUrls from "../api/urls/";
import { ButtonNew, Checkbox, CustomModal, Input } from "../components";
import { ClientLogo, LeaftechLogo } from "../components/CompanyLogo";
import { PasswordInput } from "../components/Input";
import LoginParticlesBackground from "../features/white-labeling/enakon/components/LoginParticlesBackground";
import { addUserToLocalStorage } from "../utils/localStorage";
import { cn } from "../utils/styling";

const Login = () => {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("loginChangeBackground"));
  const theme = useTheme();

  const { i18n, t } = useTranslation();
  const alternativeLanguage = i18n.language === "en" ? "de" : "en";

  const dispatch = useDispatch();
  const { isInactivelyLoggedOut, user } = useSelector((store) => store.user);
  const [isLoading, setIsLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const name = useRef("");
  const password = useRef("");
  const hanleSubmit = async (e) => {
    e.preventDefault();
    if (!name.current?.value | !password.current?.value | !consent) {
      toast.error(t("Login.form.fieldsMustBeFilled"));
      return;
    }
    const values = {
      grant_type: "password",
      password: password.current?.value,
      username: name.current?.value,
    };
    setIsLoading(true);
    loginUser(values)
      .then((resp) => {
        setIsLoading(false);
        dispatch(setUser(resp));
        addUserToLocalStorage(resp);
        toast.success(t("Login.welcome"));
      })
      .catch((error) => {
        setIsLoading(false);
        const defaultErrorMessage = "sorry! there was a problem on our end!";
        const msg = error.response.status === 401 ? "username or password is wrong!" : defaultErrorMessage;
        toast.error(msg);
      });
  };

  const setModalOpen = (modalState) => dispatch(setIsInactivelyLoggedOut(modalState));

  if (user) return <Navigate to="/" />;
  const loginLayout = theme.login.layout;

  return (
    <>
      <Helmet>
        <title>Login | {theme.clientDisplayName}</title>
      </Helmet>
      <section className={cn(loginLayout.sectionClassName)}>
        {/* Background image */}
        <img
          alt="Login Background"
          className={loginLayout.backgroundImageClassName}
          src={isSmallScreen ? theme.logoLooper.croppedSrc : theme.logoLooper.lgSrc}
        />
        {theme.login.particles && (
          <LoginParticlesBackground className={loginLayout.particlesClassName} options={theme.login.particles} />
        )}
        {loginLayout.overlayClassName && <div aria-hidden className={loginLayout.overlayClassName} />}
        {/* Logo */}
        <div className="absolute right-0 z-login_form p-6 text-center">
          <p className="uppercase font-ds-body-semibold-md">{t("Login.logoText")}</p>
          <LeaftechLogo imgProps={{ className: "mt-1" }} />
        </div>
        <ButtonNew
          className="absolute right-50 top-5 z-login_form !uppercase"
          onClick={() => {
            localStorage.setItem("language", alternativeLanguage);
            i18n.changeLanguage(alternativeLanguage);
          }}
          size="lg"
          variant="tertiary"
        >
          {alternativeLanguage}
        </ButtonNew>
        {/* Form */}
        <form className={loginLayout.formClassName}>
          {!getIsDefaultTheme(theme.name) && (
            <div className={loginLayout.clientLogoClassName}>
              <ClientLogo imgProps={{ className: "h-12" }} />
            </div>
          )}
          <h1 className="font-ds-display-lg">{t("Login.title")}</h1>
          <span className="font-ds-headline-md mt-2 inline-block">{t("Login.subtitle")}</span>
          {/* inputs-container */}
          <div className="mt-10 w-full text-right">
            {/* username */}
            <Input
              className="mb-4 block w-full "
              height="4.8rem"
              inputRef={name}
              label={t("Login.form.username.placeholder")}
              placeholder={t("Login.form.username.placeholder")}
            />
            {/* password */}
            <PasswordInput
              className="mb-4 block w-full"
              height="4.8rem"
              inputRef={password}
              label={t("Login.form.password.placeholder")}
              placeholder={t("Login.form.password.placeholder")}
            />
            <a className="text-lds-link-primary font-ds-body-bold-md" href={apiUrls.user.contactUrl}>
              {t("Login.form.forgotText")}
            </a>
          </div>
          {/* Agree to terms and conditions */}
          <div className="mb-4 mt-10 ">
            <Checkbox
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
              }}
            >
              <span className="flex items-center">
                <span className="mr-1 inline-block cursor-pointer font-ds-body-semibold-md">
                  {t("Login.form.consent")}
                </span>
                <Link className="flex items-center" to="/privacyPolicy">
                  <span className="text-lds-link-primary font-ds-body-bold-md">
                    {t("Login.form.toTermsAndConditions")}
                  </span>
                </Link>
              </span>
            </Checkbox>
          </div>
          <ButtonNew disabled={isLoading} onClick={hanleSubmit} size="lg" type="submit" variant="primary">
            <p className="font-ds-body-semibold-xl">{t("Login.form.signInText")}</p>
          </ButtonNew>
          <div className="mt-4">
            <span className="font-ds-body-semibold-md">{t("Login.form.noAccount")}</span>
            <a
              className="ml-1 inline-block text-lds-link-primary font-ds-body-bold-md"
              href={apiUrls.user.contactUrl}
              rel="noreferrer"
              target="_blank"
            >
              {t("Login.form.help")}
            </a>
          </div>
        </form>
        <CustomModal
          className="relative"
          height="21.6rem"
          modalOpen={isInactivelyLoggedOut}
          setModalOpen={setModalOpen}
        >
          <p className="font-ds-headline-md text-lds-foreground-primary">{t("SharedLayout.forceLogoutTitle")}</p>
          <p className="mt-1 text-lds-foreground-muted font-ds-body-regular-md">{t("SharedLayout.forceLogoutText")}</p>
          <ButtonNew
            className="absolute left-1/2 mt-8 capitalize"
            onClick={() => setModalOpen(false)}
            size="md"
            style={{ transform: "translateX(-50%)" }}
            type="button"
            variant="primary"
          >
            {t("Login.login")}
          </ButtonNew>
        </CustomModal>
      </section>
    </>
  );
};

export default Login;
